// Shared source of truth for where a successful login should land the user.
//
// Used by two callers that need to agree on the same decision:
// - AuthContext.initAuth (src/context/auth-context.tsx) — popup-based Google login and normal
//   email/password login, where the "current page" at success time IS the page that started
//   the flow (no full navigation happened).
// - The Discord OAuth callback (src/pages/auth/discord/callback.tsx) — a redirect-based flow
//   where the browser leaves the origin page entirely, so the callback substitutes the origin
//   page's `returnTo` (carried through the OAuth `state`) in place of `router.pathname`.
//
// Certain pages have their own post-login UX (finishing a trial signup, staying on pricing to
// complete checkout, etc.) and should NOT bounce the user to the dashboard. The affiliates page
// has its own dashboard destination. Everywhere else, a successful login lands on the dashboard.

import { SUPPORTED_LOCALES } from '@/utils/seo';

// `/preview` is the QA entry for an unpublished trial page (/preview/trial?key=<key>); it renders
// the same LP components, so it owns its post-signup screen exactly like /trial does.
// `/tim` é a LP da campanha do parceiro (SCRUM-2050) e é dona da própria tela
// pós-resgate, `/tim/obrigado` — daí o prefixo, que cobre as duas. Sem estar aqui,
// o `initAuth` disparado pelo login pós-resgate empurrava a pessoa para o dashboard
// por cima da confirmação, que é onde estão os dias e o botão de download.
const STAY_ON_LOGIN_PATH_PREFIXES = ['/trial', '/competitors', '/preview', '/tim'];
// /auth/desktop has its own post-login UX too: on success it hands the login-token off to the
// desktop client's loopback listener via a full navigation, not a push to the dashboard — see
// src/pages/auth/desktop.tsx.
//
// /auth/discord/callback is here for the same reason, and it is not a page the visitor ever chose
// to be on: it is a technical hop that already knows where to send them, from the `returnTo` it
// carried through the OAuth `state`. `initAuth` cannot know that — it judges by `router.pathname`,
// which here is the hop itself — so leaving this route out made it push to the dashboard while the
// callback replaced with `returnTo`. Its push is not awaited, so it settled last and won: signing in
// with Discord from /pricing flashed /pricing and then landed on the dashboard, losing a checkout in
// progress. Google never showed this because its popup leaves the origin page mounted, so there is
// only ever one navigation.
const STAY_ON_LOGIN_EXACT_PATHS = ['/pricing', '/boost-fps', '/trial-1day', '/you-found-it', '/auth/desktop', '/auth/discord/callback'];

// Next serves nine of the ten locales under a path prefix, so the page a visitor started from is
// `/pt/trial`, not `/trial`. The Discord callback receives that real path through the OAuth
// `state`, so every comparison here has to happen on the locale-free path or a non-English
// visitor is bounced off the page that owns their post-login screen.
// The query and hash come off first, and that is not tidiness: the Discord callback feeds this
// `window.location.pathname + search`, so the locale can be the WHOLE path — `/pt?utm_source=meta`
// for a Portuguese visitor on the homepage of a campaign. Examining the raw string would read
// `pt?utm_source=meta` as the first segment, match no locale, and hand `router.replace` a path it
// prefixes a second time into `/pt/pt?...` — a 404 on exactly the traffic a campaign pays for.
export function stripLocalePrefix(pathname: string): string {
  const suffixStart = pathname.search(/[?#]/);
  const path = suffixStart === -1 ? pathname : pathname.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? '' : pathname.slice(suffixStart);

  const [, firstSegment] = path.split('/');
  if (!firstSegment || !SUPPORTED_LOCALES.includes(firstSegment)) return pathname;

  const stripped = path.slice(firstSegment.length + 1);
  return `${stripped.startsWith('/') ? stripped : `/${stripped}`}${suffix}`;
}

export function isStayOnLoginPath(pathname: string): boolean {
  const path = stripLocalePrefix(pathname);
  if (STAY_ON_LOGIN_EXACT_PATHS.includes(path)) return true;
  return STAY_ON_LOGIN_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// Returns the path a successful login should redirect to, or `null` when the given page has
// its own post-login UX and the caller should stay put instead of redirecting.
export function getPostLoginRedirectPath(pathname: string): string | null {
  if (isStayOnLoginPath(pathname)) return null;
  if (stripLocalePrefix(pathname) === '/affiliates') return '/dashboard/affiliate';
  return '/dashboard/overview';
}
